<template>
    <div class="flex flex-col gap-4">

        <!-- Address Search -->
        <div v-if="canEditLocation" class="relative">
            <label class="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
                {{ $t("Search address") }}
            </label>
            <div class="relative">
                <input v-model="query" type="search" :placeholder="$t('Search for an address') + '...'"
                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-600 dark:placeholder-neutral-400 dark:text-white pr-10" />
                <div class="absolute right-3 top-1/2 -translate-y-1/2">
                    <svg v-if="!loading" class="text-gray-400 h-4 w-4 fill-current" viewBox="0 0 56.966 56.966">
                        <path
                            d="M55.146,51.887L41.588,37.786c3.486-4.144,5.396-9.358,5.396-14.786c0-12.682-10.318-23-23-23s-23,10.318-23,23s10.318,23,23,23c4.761,0,9.298-1.436,13.177-4.162l13.661,14.208c0.571,0.593,1.339,0.92,2.162,0.92c0.779,0,1.518-0.297,2.079-0.837C56.255,54.982,56.293,53.08,55.146,51.887z M23.984,6c9.374,0,17,7.626,17,17s-7.626,17-17,17s-17-7.626-17-17S14.61,6,23.984,6z" />
                    </svg>
                    <svg v-else class="animate-spin h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                </div>
            </div>

            <!-- Dropdown results -->
            <div v-if="results.length"
                class="absolute z-20 mt-1 w-full bg-white dark:bg-neutral-700 border border-gray-300 dark:border-neutral-600 rounded-lg shadow-lg overflow-hidden">
                <div v-for="result in results" :key="result.properties.place_id" @click="selectResult(result)"
                    class="px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-neutral-600 cursor-pointer border-b border-gray-100 dark:border-neutral-600 last:border-none transition">
                    <p class="text-sm font-medium text-gray-800 dark:text-white">{{
                        result.properties.display_name.split(',')[0] }}</p>
                    <p class="text-xs text-gray-400 dark:text-neutral-400 truncate">{{ result.properties.display_name }}</p>
                </div>
            </div>

            <div v-if="noResults"
                class="absolute z-20 mt-1 w-full bg-white dark:bg-neutral-700 border border-gray-300 dark:border-neutral-600 rounded-lg shadow-lg px-4 py-3">
                <p class="text-sm text-gray-500 dark:text-neutral-400">{{ $t('No results found') }}</p>
            </div>

            <div class="relative flex items-center gap-3 mt-4">
                <div class="flex-1 h-px bg-gray-300 dark:bg-neutral-600"></div>
                <span class="text-xs text-gray-400 dark:text-neutral-500 whitespace-nowrap">or</span>
                <div class="flex-1 h-px bg-gray-300 dark:bg-neutral-600"></div>
            </div>
        </div>

        <div v-else
                class="p-4 mb-4 flex items-start bg-gray-50 rounded-lg border border-gray-200 dark:border-neutral-600 dark:bg-neutral-800 shadow-sm gap-4 flex-col sm:flex-row md:flex-col lg:flex-row">
                <div class="flex gap-4 items-center">
                    <div class="flex-1">
                        <span class="text-base font-semibold text-gray-800 dark:text-white capitalize">
                            {{ $t('Location cannot be editted') }}
                        </span>
                        <div class="text-xs text-gray-600 dark:text-neutral-300 mt-1">
                            {{ $t('This location cannot be editted') }}.
                        </div>
                    </div>
                </div>
            </div>

        <div class="w-full flex flex-col md:flex-row gap-4">
            <div class="w-full">
                <label for="street" class="block mb-1 text-sm font-medium text-gray-900 dark:text-white">{{ $t("Street")
                }}</label>
                <input type="text" id="street" name="street" v-model="street" :disabled="!canEditLocation"
                    :class="[!canEditLocation ? 'border-gray-100 bg-gray-200 cursor-not-allowed dark:bg-neutral-800 dark:border-neutral-600' : '']"
                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-600 dark:placeholder-neutral-400 dark:text-white"
                    :placeholder="$t('Enter street') + '...'" required />
            </div>
            <div class="w-full">
                <label for="city" class="block mb-1 text-sm font-medium text-gray-900 dark:text-white">{{ $t("City")
                }}</label>
                <input type="text" id="city" name="city" v-model="city" :disabled="!canEditLocation"
                    :class="[!canEditLocation ? 'border-gray-100 bg-gray-200 cursor-not-allowed dark:bg-neutral-800 dark:border-neutral-600' : '']"
                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-600 dark:placeholder-neutral-400 dark:text-white"
                    :placeholder="$t('Enter city') + '...'" required />
            </div>
        </div>

        <div class="w-full flex flex-col md:flex-row gap-4">
            <div class="w-full">
                <label for="postcode" class="block mb-1 text-sm font-medium text-gray-900 dark:text-white">{{ $t("Postcode")
                }}</label>
                <input type="text" id="postcode" name="postcode" v-model="postcode" :disabled="!canEditLocation"
                    :class="[!canEditLocation ? 'border-gray-100 bg-gray-200 cursor-not-allowed dark:bg-neutral-800 dark:border-neutral-600' : '']"
                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-600 dark:placeholder-neutral-400 dark:text-white"
                    :placeholder="$t('Enter postcode') + '...'" required />
            </div>
            <div class="w-full">
                <label for="country" class="block mb-1 text-sm font-medium text-gray-900 dark:text-white">{{ $t("Country")
                }} (NL, EN, DE)</label>
                <input type="text" id="country" name="country" v-model="country" :disabled="!canEditLocation"
                    :class="[!canEditLocation ? 'border-gray-100 bg-gray-200 cursor-not-allowed dark:bg-neutral-800 dark:border-neutral-600' : '']"
                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-600 dark:placeholder-neutral-400 dark:text-white"
                    :placeholder="$t('Enter country') + '...'" required />
            </div>
        </div>

        <div class="w-full flex flex-col md:flex-row gap-4">
            <div class="w-full">
                <label for="lat" class="block mb-1 text-sm font-medium text-gray-900 dark:text-white">{{ $t("Lat")
                }}</label>
                <input type="text" id="lat" name="lat" v-model="lat" :disabled="!canEditLocation"
                    :class="[!canEditLocation ? 'border-gray-100 bg-gray-200 cursor-not-allowed dark:bg-neutral-800 dark:border-neutral-600' : '']"
                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-600 dark:placeholder-neutral-400 dark:text-white"
                    :placeholder="$t('Enter latitude') + '...'" required />
            </div>
            <div class="w-full">
                <label for="long" class="block mb-1 text-sm font-medium text-gray-900 dark:text-white">{{ $t("Long")
                }}</label>
                <input type="text" id="long" name="long" v-model="long" :disabled="!canEditLocation"
                    :class="[!canEditLocation ? 'border-gray-100 bg-gray-200 cursor-not-allowed dark:bg-neutral-800 dark:border-neutral-600' : '']"
                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-600 dark:placeholder-neutral-400 dark:text-white"
                    :placeholder="$t('Enter longitude') + '...'" required />
            </div>
        </div>

        <!-- Map -->
        <div v-if="lat && long"
            class="relative rounded-lg overflow-hidden border border-gray-300 dark:border-neutral-600 h-64 w-full">

            <!-- Crosshair -->
            <div class="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                <svg class="w-6 h-6 drop-shadow" :class="mapMoved ? 'text-indigo-500' : 'text-primary'" fill="none"
                    viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" />
                    <line x1="12" y1="2" x2="12" y2="8" stroke="currentColor" />
                    <line x1="12" y1="16" x2="12" y2="22" stroke="currentColor" />
                    <line x1="2" y1="12" x2="8" y2="12" stroke="currentColor" />
                    <line x1="16" y1="12" x2="22" y2="12" stroke="currentColor" />
                </svg>
            </div>

            <!-- Update button -->
            <div v-if="mapMoved && canEditLocation" class="absolute bottom-3 left-3 z-10">
                <button @click="commitMapCenter" type="button"
                    class="flex items-center cursor-pointer gap-2 bg-white dark:bg-neutral-800 border border-gray-300 dark:border-neutral-600 text-gray-800 dark:text-white text-xs font-medium px-4 py-2 rounded-full shadow-lg hover:bg-gray-50 dark:hover:bg-neutral-700 transition">
                    <svg class="w-3.5 h-3.5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"
                        stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {{ $t('Use this location') }}
                </button>
            </div>

            <div class="h-full w-full" ref="mapContainer"></div>
        </div>

    </div>
</template>

<script>
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

export default {
    name: 'LocationFields',

    props: {
        canEditLocation: {
            type: Boolean,
            default: true,
        },
        location: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            query: '',
            results: [],
            noResults: false,
            loading: false,
            debounceTimer: null,
            street: this.location?.street || '',
            city: this.location?.city || '',
            postcode: this.location?.postcode || '',
            country: this.location?.country || '',
            lat: this.location?.lat ? String(this.location.lat) : '',
            long: this.location?.long ? String(this.location.long) : '',
            map: null,
            isProgrammaticMove: false,
            mapMoved: false,
            pendingCenter: null, // holds the center after map move, before commit
        }
    },

    mounted() {
        if (this.lat && this.long) {
            this.$nextTick(() => this.initMap())
        }
    },

    watch: {
        location: {
            immediate: false,
            deep: true,
            handler(val) {
                if (!val) return
                this.street = val.street || ''
                this.city = val.city || ''
                this.postcode = val.postcode || ''
                this.country = val.country || ''
                this.lat = val.lat ? String(val.lat) : ''
                this.long = val.long ? String(val.long) : ''
            }
        },

        query() {
            clearTimeout(this.debounceTimer)
            if (!this.query.trim()) {
                this.results = []
                this.noResults = false
                return
            }
            this.debounceTimer = setTimeout(() => this.search(), 400)
        },

        lat() { this.onCoordsManuallyChanged() },
        long() { this.onCoordsManuallyChanged() },
    },

    methods: {
        async search() {
            this.loading = true
            this.noResults = false
            try {
                const res = await fetch(
                    `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(this.query)}&format=geojson&addressdetails=1`,
                    { headers: { 'Accept-Language': 'en' } }
                )
                const data = await res.json()
                this.results = data.features || []
                this.noResults = this.results.length === 0
            } catch (e) {
                console.error(e)
            } finally {
                this.loading = false
            }
        },

        selectResult(result) {
            const addr = result.properties.address || {}
            const [lng, lat] = result.geometry.coordinates

            this.street = [addr.road, addr.house_number].filter(Boolean).join(' ')
            this.city = addr.city || addr.town || addr.village || ''
            this.postcode = addr.postcode || ''
            this.country = addr.country_code?.toUpperCase() || ''
            this.lat = String(lat)
            this.long = String(lng)

            this.query = ''
            this.results = []
            this.noResults = false
            this.mapMoved = false

            this.$emit('changedValue', this.formData())
        },

        onCoordsManuallyChanged() {
            const lat = parseFloat(this.lat)
            const lng = parseFloat(this.long)
            if (isNaN(lat) || isNaN(lng)) return

            if (!this.map) {
                this.$nextTick(() => this.initMap())
                return
            }

            this.isProgrammaticMove = true
            this.mapMoved = false
            this.map.flyTo({ center: [lng, lat], zoom: 15 })
        },

        initMap() {
            if (!this.$refs.mapContainer) return

            const lat = parseFloat(this.lat)
            const lng = parseFloat(this.long)
            if (isNaN(lat) || isNaN(lng)) return

            this.map = new maplibregl.Map({
                container: this.$refs.mapContainer,
                style: 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json',
                center: [lng, lat],
                zoom: 15,
            })

            this.map.addControl(new maplibregl.NavigationControl(), 'bottom-right')

            this.map.on('movestart', () => {
                if (!this.isProgrammaticMove) {
                    this.mapMoved = false
                }
            })

            this.map.on('moveend', () => {
                if (this.isProgrammaticMove) {
                    this.isProgrammaticMove = false
                    this.mapMoved = false
                    return
                }

                // Store the pending center but don't commit yet
                this.pendingCenter = this.map.getCenter()
                this.mapMoved = true
            })
        },

        async commitMapCenter() {
            if (!this.pendingCenter) return

            const { lat, lng } = this.pendingCenter

            this.lat = String(lat.toFixed(7))
            this.long = String(lng.toFixed(7))
            this.mapMoved = false

            this.$emit('changedValue', this.formData())

            // Reverse geocode
            try {
                const res = await fetch(
                    `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
                    { headers: { 'Accept-Language': 'en' } }
                )
                const data = await res.json()
                const addr = data.address || {}

                this.street = [addr.road, addr.house_number].filter(Boolean).join(' ')
                this.city = addr.city || addr.town || addr.village || ''
                this.postcode = addr.postcode || ''
                this.country = addr.country_code?.toUpperCase() || ''

                this.$emit('changedValue', this.formData())
            } catch (e) {
                console.error('Reverse geocode failed', e)
            }
        },

        formData() {
            return {
                street: this.street,
                city: this.city,
                postcode: this.postcode,
                country: this.country,
                lat: this.lat,
                long: this.long,
            }
        }
    },

    beforeDestroy() {
        if (this.map) this.map.remove()
    }
}
</script>